import React from 'react';
import find from 'lodash/find';

import { Cell, Grid } from 'formlabs-web-components/Grid';
import { Picture } from 'formlabs-web-components/Media';
import { Button } from 'formlabs-web-components/Link';

import CheckmarkList from '../../content/CheckmarkList';

import bfKitImage from '../../../../../public/fk-f3-01.png';
import serviceImage1 from '../../../../../public/service1.svg';
import serviceImage2 from '../../../../../public/service2.svg';
import serviceImage3 from '../../../../../public/service3.svg';
import serviceImage4 from '../../../../../public/service4.svg';
import serviceImage5 from '../../../../../public/service5.svg';
import serviceImage6 from '../../../../../public/service6.svg';
import icon from '../../../../../public/icons/information.svg';

import '../ProductDetailPlugin.module.css';

export const getProductDetails = (t, bundleOptions, path) => [
  {
    sku: 'PKG-F3-WSVC-BASIC',
    productTitle: t('Form 3 Package without Service'),
    additionalDescription: (
      <p>
        <div styleName="bio-container">
          {t('Looking for biocompatible materials?')}
          <div styleName="bio-question-icon">
            <Picture largeImage={icon} />
            {'  '}
          </div>
        </div>
        <Button
          color="borderless-blue"
          arrow="right-caret"
          to={`https://dental.formlabs.com${path}form-3b/`}
        >
          {t('Learn about our dental 3D printer, the Form 3B')}
        </Button>
      </p>
    ),
    steps: [
      {
        order: 0,
        type: 'option',
        title: t('Add your Service Plan'),
        progressTitle: t('Service Plans'),
        analytics: {
          pageName: 'pdp_product_configuration_service_plans',
          stepName: 'service_plans'
        },
        callback: () => null,
        sections: [
          {
            title: t('Formlabs Pro Service Plan'),
            subtitle: t(
              'Service plans to set you up for success and support you along the way'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <Grid direction="x">
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage1} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Priority Access to Pro Service Engineers')}
                      </strong>
                      <br />
                      {t(
                        'A direct line to a Pro Service Engineer and prioritized email support from our dedicated staff'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage2} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Customized Training')}</strong>
                      <br />
                      {t(
                        'A 60-minute training session over video with a Pro Service Engineer, customized to you'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage3} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Hot Swaps for Hardware Replacements')}
                      </strong>
                      <br />
                      {t(
                        'Ensure constant uptime: Formlabs will ship you a replacement printer to keep you printing if your printer needs to be repaired or replaced.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage4} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Warranty for the Duration of the Plan')}
                      </strong>
                      <br />
                      {t(
                        'The Pro Service Plan extends the one-year warranty included with your printer to the full duration of the chosen plan.'
                      )}
                    </p>
                  </Cell>
                </Grid>
              </Cell>
            ),
            options: [
              {
                id: 7001029,
                type: 'radio',
                recommended: 1,
                fetchDetails: true
              }
            ],
            required: false,
            combineOptions: false
          },
          {
            title: t('Basic 1-Year Warranty Plan'),
            subtitle: t(
              'Our included warranty and access to support resources'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <CheckmarkList
                  width={1}
                  items={[
                    t('One-year warranty'),
                    t('Email support'),
                    t('On-demand resources through our support site')
                  ]}
                  light
                  narrow
                />
              </Cell>
            ),
            options: [
              {
                type: 'checkbox',
                fullOption: {
                  product_links: [{ name: 'Included', option_id: 1000 }],
                  option_id: 0
                },
                productName: t('Included')
              }
            ],
            required: true,
            combineOptions: false
          }
        ]
      },
      {
        order: 1,
        type: 'option',
        title: t('Choose your Post-Processing'),
        progressTitle: t('Post-Processing'),
        analytics: {
          pageName: 'pdp_product_configuration_post_processing',
          stepName: 'post_processing'
        },
        callback: () => null,
        sections: [
          {
            title: t('Automated Post-Processing'),
            subtitle: t(
              'Stronger parts, less effort with Form Wash and Form Cure'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <CheckmarkList
                  width={1}
                  items={[
                    t(
                      'Form Wash automates the print washing process for consistent, thorough, no-mess cleaning.'
                    ),
                    t(
                      'Parts can be transferred directly from the printer to Form Wash and either rinsed directly on the build platform or removed and placed in the wash basket.'
                    ),
                    t(
                      'Post-curing with Form Cure maximizes material properties for stereolithography prints, improving parts’ strength and performance.'
                    ),
                    t(
                      'Form Cure precisely combines heat and 405 nm light to consistently post-cure prints'
                    )
                  ]}
                  light={true}
                />
              </Cell>
            ),
            options: [
              {
                id: 7001028,
                transformFn: bundle => {
                  if (bundle.has(7001029) && bundle.get(7001029).size > 0) {
                    const postProcessingReduced = find(
                      bundleOptions,
                      ({ product_links }) =>
                        RegExp('FH-(WA|CU)-01.sp.sp').test(product_links[0].sku)
                    );
                    const postProcessing = find(
                      bundleOptions,
                      ({ product_links }) =>
                        RegExp('FH-(WA|CU)-01').test(product_links[0].sku)
                    );
                    if (!postProcessingReduced) return postProcessing;
                    let originalPrice = 0;
                    if (postProcessing) {
                      originalPrice = postProcessing.product_links.reduce(
                        (sum, product) => {
                          return sum + product.price;
                        },
                        0
                      );
                    }
                    postProcessingReduced.originalPrice = originalPrice;
                    return postProcessingReduced;
                  } else {
                    return find(bundleOptions, ({ product_links }) =>
                      RegExp('FH-(WA|CU)-01').test(product_links[0].sku)
                    );
                  }
                },
                type: 'checkbox',
                images: [
                  'https://formlabs.com/checkout-media/catalog/product/400x400/f/h/fh-wa-01_sq.png',
                  'https://formlabs.com/checkout-media/catalog/product/400x400/f/h/fh-cu-01_sq.png'
                ],
                productName: t('Form Wash & Form Cure') // (optional)
              }
            ],
            required: false,
            combineOptions: true
          },
          {
            title: t('Basic Finish Kit'),
            subtitle: t(
              'The Form 3 Finish Kit keeps your workspace organized and includes essential tools for finishing'
            ),
            copy: (
              <>
                <Cell mediumSize="12" smallOrder={2}>
                  <Grid direction="x">
                    <Cell mediumSize="12" smallOrder={2}>
                      <CheckmarkList
                        width={1}
                        items={[
                          t('Rinse station'),
                          t('2 x rinse buckets'),
                          t('Rinse basket'),
                          t('Rinse bottle'),
                          t('Finishing tray'),
                          t('Rinse basket')
                        ]}
                        light
                        narrow
                      />
                    </Cell>
                    <Cell mediumSize="12" smallOrder={3}>
                      <CheckmarkList
                        width={1}
                        items={[
                          t('Tweezers'),
                          t('Scraper'),
                          t('Removal tool'),
                          t('Flush cutters'),
                          t('Jig'),
                          t('Disposable nitrile gloves')
                        ]}
                        light
                        narrow
                      />
                    </Cell>
                  </Grid>
                </Cell>
              </>
            ),
            options: [
              {
                id: 7001027,
                type: 'checkbox',
                images: [bfKitImage]
              }
            ],
            required: true,
            combineOptions: false
          }
        ]
      },
      {
        order: 2,
        type: 'call-us',
        title: t('Complete Your Order'),
        analytics: {
          pageName: 'pdp_product_configuration_call_to_order',
          stepName: 'call_to_order'
        }
      }
    ]
  },
  {
    sku: 'PKG-F3BL-BASIC',
    productTitle: t('Form 3BL Package without Service'),
    toggleCallUs: 7001057,
    steps: [
      {
        order: 0,
        type: 'option',
        title: t('Add your Service Plan'),
        progressTitle: t('Service Plans'),
        analytics: {
          pageName: 'pdp_product_configuration_service_plans',
          stepName: 'service_plans'
        },
        callback: () => null,
        sections: [
          {
            title: t('Formlabs Dental Service Plan'),
            subtitle: t('Premium 3D printing support, made for dental'),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <Grid direction="x">
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage2} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Proactive Check-Ins')}</strong>
                      <br />
                      {t(
                        'We’ll check in on your process and equipment diagnostics every six months so you can stay focused on running your business.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage6} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Phone Support')}</strong>
                      <br />
                      {t(
                        'You now have a direct line to our Certified Dental Agents to help you one-on-one. Call us Monday through Friday. 9 am - 6 pm ET and CET.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage5} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Priority Email Support')}</strong>
                      <br />
                      {t(
                        'You’re our priority in email support. Shoot us an email, and a Certified Dental Agent will respond as soon as possible.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage3} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Hot Swaps for Hardware Replacements')}
                      </strong>
                      <br />
                      {t(
                        'Ensure constant uptime: Formlabs will ship you a replacement printer to keep you printing if your printer needs to be repaired or replaced.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage4} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Extended Warranty')}</strong>
                      <br />
                      {t(
                        'The Pro Service Plan extends the one-year warranty included with your printer to the full duration of the chosen plan.'
                      )}
                    </p>
                  </Cell>
                </Grid>
              </Cell>
            ),
            options: [
              {
                id: 7001057,
                type: 'radio',
                recommended: 1,
                fetchDetails: true
              }
            ],
            required: false,
            combineOptions: false
          },
          {
            title: t('Basic 1-Year Warranty Plan'),
            subtitle: t(
              'Our included warranty and access to support resources'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <CheckmarkList
                  width={1}
                  items={[
                    t('One-year warranty'),
                    t('Email support'),
                    t('On-demand resources through our support site')
                  ]}
                  light
                  narrow
                />
              </Cell>
            ),
            options: [
              {
                type: 'checkbox',
                fullOption: {
                  product_links: [{ name: 'Included', option_id: 7001029 }],
                  option_id: 0
                },
                productName: t('Included')
              }
            ],
            required: true,
            combineOptions: false
          }
        ]
      },
      {
        order: 1,
        type: 'call-us',
        title: t('Complete Your Order'),
        analytics: {
          pageName: 'pdp_product_configuration_call_to_order',
          stepName: 'call_to_order'
        }
      }
    ]
  },
  {
    sku: 'PKG-F3L-BASIC',
    productTitle: t('Form 3L Package without Service'),
    additionalDescription: (
      <p>
        <div styleName="bio-container">
          {t('Looking for biocompatible materials?')}
          <div styleName="bio-question-icon">
            <Picture largeImage={icon} />
            {'  '}
          </div>
        </div>
        <Button
          color="borderless-blue"
          arrow="right-caret"
          to={`https://dental.formlabs.com${path}form-3bl/`}
        >
          {t('Learn about our dental 3D printer, the Form 3BL')}
        </Button>
      </p>
    ),
    toggleCallUs: 7001082,
    steps: [
      {
        order: 0,
        type: 'option',
        title: t('Add your Service Plan'),
        progressTitle: t('Service Plans'),
        analytics: {
          pageName: 'pdp_product_configuration_service_plans',
          stepName: 'service_plans'
        },
        callback: () => null,
        sections: [
          {
            title: t('Formlabs Pro Service Plan'),
            subtitle: t(
              'Service plans to set you up for success and support you along the way'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <Grid direction="x">
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage1} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Priority Access to Pro Service Engineers')}
                      </strong>
                      <br />
                      {t(
                        'A direct line to a Pro Service Engineer by telephone and prioritized email support from our dedicated staff.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage2} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Customized Training')}</strong>
                      <br />
                      {t(
                        'A 60-minute training session over video with a Pro Service Engineer, customized to you.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage3} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>
                        {t('Hot Swaps for Hardware Replacements')}
                      </strong>
                      <br />
                      {t(
                        'Ensure constant uptime: Formlabs will ship you a replacement printer to keep you printing if your printer needs to be repaired or replaced.'
                      )}
                    </p>
                  </Cell>
                  <Cell smallSize={6}>
                    <Picture largeImage={serviceImage4} />
                  </Cell>
                  <Cell smallSize={18}>
                    <p>
                      <strong>{t('Extended Warranty')}</strong>
                      <br />
                      {t(
                        'The Pro Service Plan extends the one-year warranty included with your printer to the full duration of the chosen plan.'
                      )}
                    </p>
                  </Cell>
                </Grid>
              </Cell>
            ),
            options: [
              {
                id: 7001082,
                type: 'radio',
                recommended: 1,
                fetchDetails: true
              }
            ],
            required: false,
            combineOptions: false
          },
          {
            title: t('Basic 1-Year Warranty Plan'),
            subtitle: t(
              'Our included warranty and access to support resources'
            ),
            copy: (
              <Cell mediumSize="12" smallOrder={2}>
                <CheckmarkList
                  width={1}
                  items={[
                    t('One-year warranty'),
                    t('Email support'),
                    t('On-demand resources through our support site')
                  ]}
                  light
                  narrow
                />
              </Cell>
            ),
            options: [
              {
                type: 'checkbox',
                fullOption: {
                  product_links: [{ name: 'Included', option_id: 7001029 }],
                  option_id: 0
                },
                productName: t('Included')
              }
            ],
            required: true,
            combineOptions: false
          }
        ]
      },
      {
        order: 1,
        type: 'call-us',
        title: t('Complete Your Order'),
        analytics: {
          pageName: 'pdp_product_configuration_call_to_order',
          stepName: 'call_to_order'
        }
      }
    ]
  }
];
